import {match} from 'ts-pattern';

// eslint-disable-next-line no-restricted-imports
import {head, path, isNil} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {testIds, warehouseRoutes, workshopRoutes} from '@dms/routes';
import {
  DmsDataGrid,
  WAREHOUSE_GRID_CODES,
  catchUnhandledDataGridActions,
  logError,
  queryParams,
} from '@dms/shared';
import {FullscreenDataGridWrapper} from '@dms/warehouse/shared-components';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

const PREDEFINED_COLUMNS = ['originDocNumber', 'serviceCaseId', 'serviceOrderId'] as const;

export function IssueNoteCorrections() {
  const navigate = useNavigate();

  const actionCallback: ActionCallback = ({actionKey, rowId, sourceSearchParams}) => {
    const id = isArray(rowId) ? head(rowId) : rowId;

    match(actionKey)
      .with('redirectDetail', 'details', () =>
        navigate(
          composePath(warehouseRoutes.issueNoteCorrectionDetailOverview, {
            queryParams: sourceSearchParams,
            params: {id},
            isSearchParamsPreserved: false,
          })
        )
      )
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <FullscreenDataGridWrapper hideCardWrapper>
      <DmsDataGrid
        gridCode={WAREHOUSE_GRID_CODES.warehouseIssueNoteCorrections}
        actionCallback={actionCallback}
        predefinedColumns={PREDEFINED_COLUMNS}
        _useAsLastResort_linkClickOverride={({rowData, defaultRedirectRoute}) => {
          const resourceId = path(['originDocNumber', 'value', 'resourceId'], rowData);

          if (isNil(resourceId)) {
            return logError('resourceId is not defined');
          }

          return match(resourceId)
            .with('WORKSHOP_SERVICE_ORDER_DETAIL', () => {
              const serviceCaseId = path(['serviceCaseId', 'value'], rowData);
              const serviceOrderId = path(['serviceOrderId', 'value'], rowData);

              if (isNil(serviceCaseId)) {
                return logError('serviceCaseId is not defined');
              }

              if (isNil(serviceOrderId)) {
                return logError('serviceOrderId is not defined');
              }

              return navigate(
                composePath(workshopRoutes.serviceCaseDetail, {
                  params: {
                    id: serviceCaseId as string,
                  },
                  queryParams: {
                    [queryParams.SERVICE_CASE_ORDER_ID]: serviceOrderId as string,
                  },
                })
              );
            })
            .otherwise(() => navigate(defaultRedirectRoute));
        }}
        data-testid={testIds.warehouse.issueNoteCorrections('list')}
      />
    </FullscreenDataGridWrapper>
  );
}
